import React, { useMemo } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { User } from '../../types/auth';

interface UserAvatarProps {
  user: User | null | undefined;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * UserAvatar component that handles user profile pictures with fallbacks
 * Generates initials-based avatars directly in the browser
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({ 
  user, 
  className = '', 
  size = 'md' 
}) => {
  // Size classes for different avatar sizes
  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12'
  };

  // Get user initials for the fallback
  const initials = useMemo(() => {
    if (!user?.name) return '?';
    
    return user.name
      .split(' ')
      .map((part: string) => part[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }, [user?.name]);

  // Fix Google profile picture URL to avoid 429 errors
  const optimizedPictureUrl = useMemo(() => {
    if (!user?.picture) return '';
    
    // Handle Google profile pictures
    if (user.picture.includes('googleusercontent.com')) {
      // Parse the URL to remove any tracking parameters
      const baseUrl = user.picture.split('=')[0];
      // Add a size parameter that works reliably
      return `${baseUrl}=s128-c`;
    }
    
    return user.picture;
  }, [user?.picture]);

  return (
    <Avatar className={`${sizeClasses[size]} ${className}`}>
      <AvatarImage 
        src={optimizedPictureUrl} 
        alt={user?.name || 'User'} 
      />
      <AvatarFallback 
        className="bg-primary text-primary-foreground"
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}; 